export interface TradeStudyChartColors {
  grid: string;
  axisTitle: string;
  title: string;
  time: string;
  timeBorder: string;
  cost: string;
  costBorder: string;
  required: string;
  requiredBorder: string;
  capacity: string;
  completion: string;
  completionBorder: string;
  materialCost: string;
  labourCost: string;
  capex: string;
  capexBorder: string;
  pieBorder: string;
  piePalette: string[];
}

const FALLBACK_COLORS: TradeStudyChartColors = {
  grid: 'rgb(100 116 139 / 14%)',
  axisTitle: '#64748b',
  title: '#334155',
  time: '#3b82f6',
  timeBorder: '#2563eb',
  cost: '#10b981',
  costBorder: '#059669',
  required: 'rgb(245 158 11 / 85%)',
  requiredBorder: '#d97706',
  capacity: 'rgb(59 130 246 / 85%)',
  completion: 'rgb(139 92 246 / 90%)',
  completionBorder: '#7c3aed',
  materialCost: 'rgb(59 130 246 / 90%)',
  labourCost: 'rgb(16 185 129 / 90%)',
  capex: 'rgb(239 68 68 / 90%)',
  capexBorder: '#dc2626',
  pieBorder: '#f8fafc',
  piePalette: [
    '#3b82f6',
    '#10b981',
    '#f59e0b',
    '#ef4444',
    '#8b5cf6',
    '#06b6d4',
    '#f97316',
    '#64748b',
  ],
};

export function truncateChartLabel(name: string, max = 52): string {
  if (name.length <= max) {
    return name;
  }
  return `${name.slice(0, max - 1)}…`;
}

function cssVariable(name: string, fallback: string): string {
  if (
    typeof document === 'undefined' ||
    typeof getComputedStyle === 'undefined'
  ) {
    return fallback;
  }

  return (
    getComputedStyle(document.documentElement).getPropertyValue(name).trim() ||
    fallback
  );
}

export function readTradeStudyChartColors(): TradeStudyChartColors {
  return {
    grid: cssVariable('--color-chart-grid', FALLBACK_COLORS.grid),
    axisTitle: cssVariable(
      '--color-chart-axis-title',
      FALLBACK_COLORS.axisTitle,
    ),
    title: cssVariable('--color-chart-title', FALLBACK_COLORS.title),
    time: cssVariable('--color-chart-time', FALLBACK_COLORS.time),
    timeBorder: cssVariable(
      '--color-chart-time-border',
      FALLBACK_COLORS.timeBorder,
    ),
    cost: cssVariable('--color-chart-cost', FALLBACK_COLORS.cost),
    costBorder: cssVariable(
      '--color-chart-cost-border',
      FALLBACK_COLORS.costBorder,
    ),
    required: cssVariable('--color-chart-required', FALLBACK_COLORS.required),
    requiredBorder: cssVariable(
      '--color-chart-required-border',
      FALLBACK_COLORS.requiredBorder,
    ),
    capacity: cssVariable('--color-chart-capacity', FALLBACK_COLORS.capacity),
    completion: cssVariable(
      '--color-chart-completion',
      FALLBACK_COLORS.completion,
    ),
    completionBorder: cssVariable(
      '--color-chart-completion-border',
      FALLBACK_COLORS.completionBorder,
    ),
    materialCost: cssVariable(
      '--color-chart-material-cost',
      FALLBACK_COLORS.materialCost,
    ),
    labourCost: cssVariable(
      '--color-chart-labour-cost',
      FALLBACK_COLORS.labourCost,
    ),
    capex: cssVariable('--color-chart-capex', FALLBACK_COLORS.capex),
    capexBorder: cssVariable(
      '--color-chart-capex-border',
      FALLBACK_COLORS.capexBorder,
    ),
    pieBorder: cssVariable(
      '--color-chart-pie-border',
      FALLBACK_COLORS.pieBorder,
    ),
    piePalette: FALLBACK_COLORS.piePalette.map((fallback, index) =>
      cssVariable(`--color-chart-pie-${index + 1}`, fallback),
    ),
  };
}

function scaleTitle(
  text: string,
  colors: TradeStudyChartColors,
): Record<string, unknown> {
  return {
    display: true,
    text,
    font: { size: 12, weight: '500' },
    color: colors.axisTitle,
    padding: { top: 4, bottom: 10, left: 0, right: 0 },
  };
}

function createGroupedBarChartOptions(
  colors: TradeStudyChartColors,
  xAxisTitle: string,
  yAxisTitle: string,
): Record<string, unknown> {
  return {
    responsive: true,
    maintainAspectRatio: false,
    resizeDelay: 0,
    animation: false,
    interaction: { mode: 'index', intersect: false },
    layout: { padding: { top: 8, right: 12, bottom: 8, left: 8 } },
    plugins: {
      legend: {
        display: true,
        position: 'top',
        align: 'end',
        labels: {
          boxWidth: 14,
          boxHeight: 14,
          padding: 18,
          usePointStyle: true,
          font: { size: 12, family: 'system-ui, sans-serif' },
        },
      },
      tooltip: {
        mode: 'index',
        intersect: false,
        padding: 10,
        titleFont: { size: 13 },
        bodyFont: { size: 12 },
      },
    },
    scales: {
      x: {
        offset: true,
        grid: { display: false },
        ticks: {
          maxRotation: 0,
          autoSkip: true,
          font: { size: 12 },
        },
        title: scaleTitle(xAxisTitle, colors),
      },
      y: {
        beginAtZero: true,
        border: { display: false },
        ticks: { font: { size: 11 }, maxTicksLimit: 10, padding: 8 },
        grid: { color: colors.grid },
        title: scaleTitle(yAxisTitle, colors),
      },
    },
    datasets: {
      bar: {
        categoryPercentage: 0.68,
        barPercentage: 0.82,
        borderRadius: 4,
      },
    },
  };
}

function createHorizontalBarChartOptions(
  colors: TradeStudyChartColors,
  xAxisTitle: string,
  yAxisTitle: string,
): Record<string, unknown> {
  return {
    indexAxis: 'y',
    responsive: true,
    maintainAspectRatio: false,
    resizeDelay: 0,
    animation: false,
    interaction: { mode: 'nearest', axis: 'y', intersect: false },
    layout: { padding: { top: 4, right: 20, bottom: 12, left: 4 } },
    plugins: {
      legend: { display: false },
      tooltip: {
        intersect: false,
        padding: 10,
        titleFont: { size: 13 },
        bodyFont: { size: 12 },
      },
    },
    scales: {
      x: {
        beginAtZero: true,
        border: { display: false },
        ticks: { font: { size: 11 }, maxTicksLimit: 8, padding: 6 },
        grid: { color: colors.grid },
        title: scaleTitle(xAxisTitle, colors),
      },
      y: {
        border: { display: false },
        ticks: {
          font: { size: 11 },
          autoSkip: false,
          padding: 10,
        },
        grid: { display: false },
        title: scaleTitle(yAxisTitle, colors),
      },
    },
    datasets: {
      bar: {
        categoryPercentage: 0.62,
        barPercentage: 0.66,
        borderRadius: 4,
      },
    },
  };
}

function createBaseHorizontalStackedBarChartOptions(
  colors: TradeStudyChartColors,
  yAxisTitle: string,
  currencyCode: string,
): Record<string, unknown> {
  return {
    indexAxis: 'y',
    responsive: true,
    maintainAspectRatio: false,
    resizeDelay: 0,
    animation: false,
    interaction: { mode: 'index', intersect: false },
    layout: { padding: { top: 6, right: 22, bottom: 12, left: 4 } },
    plugins: {
      legend: {
        display: true,
        position: 'top',
        align: 'center',
        labels: {
          boxWidth: 12,
          padding: 14,
          usePointStyle: true,
          font: { size: 12 },
        },
      },
      tooltip: {
        mode: 'index',
        intersect: false,
        padding: 10,
      },
    },
    scales: {
      x: {
        stacked: true,
        beginAtZero: true,
        border: { display: false },
        ticks: { font: { size: 11 }, maxTicksLimit: 10, padding: 6 },
        grid: { color: colors.grid },
        title: scaleTitle(
          `${currencyCode} (stacked: material + labour + capex)`,
          colors,
        ),
      },
      y: {
        stacked: true,
        border: { display: false },
        ticks: {
          font: { size: 11 },
          autoSkip: false,
          padding: 10,
        },
        grid: { display: false },
        title: scaleTitle(yAxisTitle, colors),
      },
    },
    datasets: {
      bar: {
        categoryPercentage: 0.75,
        barPercentage: 0.88,
        borderRadius: 3,
      },
    },
  };
}

export function createCapacityGroupedBarChartOptions(
  colors: TradeStudyChartColors,
): Record<string, unknown> {
  return createGroupedBarChartOptions(
    colors,
    'Shift scenario',
    'Hours per month',
  );
}

export function createCompletionGroupedBarChartOptions(
  colors: TradeStudyChartColors,
): Record<string, unknown> {
  return createGroupedBarChartOptions(
    colors,
    'Shift scenario',
    'Months to complete (program labor ÷ capacity)',
  );
}

export function createTimePerPartBarChartOptions(
  colors: TradeStudyChartColors,
  currencyCode: string,
): Record<string, unknown> {
  return createHorizontalBarChartOptions(
    colors,
    `Labor hours (subtotal cost ÷ loaded labor ${currencyCode}/hr, per part)`,
    'Part (root line item)',
  );
}

export function createCostPerPartBarChartOptions(
  colors: TradeStudyChartColors,
  currencyCode: string,
): Record<string, unknown> {
  return createHorizontalBarChartOptions(
    colors,
    `Subtotal cost (${currencyCode})`,
    'Part (root line item)',
  );
}

export function createTimePerProcessBarChartOptions(
  colors: TradeStudyChartColors,
  currencyCode: string,
): Record<string, unknown> {
  return createHorizontalBarChartOptions(
    colors,
    `Labor hours (subtotal cost ÷ loaded labor ${currencyCode}/hr, per process)`,
    'Process (child line item)',
  );
}

export function createCostPerProcessBarChartOptions(
  colors: TradeStudyChartColors,
  currencyCode: string,
): Record<string, unknown> {
  return createHorizontalBarChartOptions(
    colors,
    `Subtotal cost (${currencyCode})`,
    'Process (child line item)',
  );
}

export function createHorizontalStackedBarChartOptions(
  colors: TradeStudyChartColors,
  currencyCode: string,
): Record<string, unknown> {
  return createBaseHorizontalStackedBarChartOptions(
    colors,
    'Part (root line item)',
    currencyCode,
  );
}

export function createHorizontalStackedProcessBarChartOptions(
  colors: TradeStudyChartColors,
  currencyCode: string,
): Record<string, unknown> {
  return createBaseHorizontalStackedBarChartOptions(
    colors,
    'Process (child line item)',
    currencyCode,
  );
}

export function createPieChartOptions(
  colors: TradeStudyChartColors,
): Record<string, unknown> {
  return {
    responsive: true,
    maintainAspectRatio: false,
    resizeDelay: 0,
    animation: false,
    layout: { padding: { top: 8, right: 12, bottom: 8, left: 12 } },
    plugins: {
      title: {
        display: true,
        font: { size: 13, weight: '600' },
        color: colors.title,
        padding: { bottom: 8 },
      },
      legend: {
        position: 'right',
        align: 'center',
        labels: {
          padding: 14,
          boxWidth: 12,
          boxHeight: 12,
          usePointStyle: true,
          font: { size: 11 },
        },
      },
      tooltip: {
        padding: 10,
        bodySpacing: 6,
      },
    },
  };
}
